import {
    Button,
    ButtonColor,
    ButtonSize,
    ButtonType,
    Container,
    ContainerSize,
    Gap,
    Heading,
    Stack,
    Text
} from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";
type ButtonProps = {}

const ButtonPage = ({ }: ButtonProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Buttons
            </Heading>

            <Text el="p"><Text el="strong">Buttons</Text> are used to trigger actions, submit forms, or navigate users.
                The Button component provides multiple variants and can be customized using props such as <Text
                    el="strong">color</Text>, <Text el="strong">size</Text>, and <Text
                        el="strong">disabled</Text>.</Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Example
            </Heading>
            <Text className="mb-4">
                React Bootstrap provides several predefined <Text el="strong">button</Text> styles. You can quickly
                apply them using the <Text el="strong">color</Text> prop to match different contexts such as primary
                actions, warnings, success states, or errors.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button color={ButtonColor.Primary}>Primary</Button>
                        <Button color={ButtonColor.Secondary}>Secondary</Button>
                        <Button color={ButtonColor.Success}>Success</Button>
                        <Button color={ButtonColor.Danger}>Danger</Button>
                        <Button color={ButtonColor.Warning}>Warning</Button>
                        <Button color={ButtonColor.Info}>Info</Button>
                        <Button color={ButtonColor.Light}>Light</Button>
                        <Button color={ButtonColor.Dark}>Dark</Button>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonColorCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Outline Buttons
            </Heading>

            <Text className="mb-4">
                <Text el="strong">Outline</Text> buttons are used when you want a less prominent, bordered style instead
                of a solid color. They are visually lighter and suitable for secondary actions.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button color={ButtonColor.OutlinePrimary}>Primary</Button>
                        <Button color={ButtonColor.OutlineSecondary}>Secondary</Button>
                        <Button color={ButtonColor.OutlineSuccess}>Success</Button>
                        <Button color={ButtonColor.OutlineDanger}>Danger</Button>
                        <Button color={ButtonColor.OutlineWarning}>Warning</Button>
                        <Button color={ButtonColor.OutlineInfo}>Info</Button>
                        <Button color={ButtonColor.OutlineLight}>Light</Button>
                        <Button color={ButtonColor.OutlineDark}>Dark</Button>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonOutlineColorCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Button Sizes
            </Heading>

            <Text className="mb-4">
                <Text el="strong">Buttons</Text> can be rendered in different sizes to fit the context of your UI.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button size={ButtonSize.Lg} color={ButtonColor.Primary}>Primary</Button>
                        <Button size={ButtonSize.Lg} color={ButtonColor.OutlineSecondary}>Secondary</Button>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonLGSizeCode}
                </CodeHighlighter>
            </CodeSnippet>

            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button size={ButtonSize.Sm} color={ButtonColor.Primary}>Primary</Button>
                        <Button size={ButtonSize.Sm} color={ButtonColor.OutlineSecondary}>Secondary</Button>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonSMSizeCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Disabled button
            </Heading>

            <Text className="mb-4">
                <Text el="strong">Disabled buttons</Text> are used to indicate actions that are currently unavailable.
                They are unclickable and styled differently to communicate inactivity.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button color={ButtonColor.Primary} disabled={true}>Primary</Button>
                        <Button color={ButtonColor.Secondary} disabled={true}>Secondary</Button>
                        <Button color={ButtonColor.OutlinePrimary} disabled={true}>Primary</Button>
                        <Button color={ButtonColor.OutlineSecondary} disabled={true}>Secondary</Button>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonDisabledCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Button Types
            </Heading>

            <Text className="mb-4">
                The <Text el="strong">type</Text> prop determines the button's behavior in forms. Use <Text el="strong">submit</Text> to submit forms, <Text el="strong">reset</Text> to reset form fields, or <Text el="strong">button</Text> (default) for regular click actions.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button type={ButtonType.Button} color={ButtonColor.Primary}>Button (Default)</Button>
                        <Button type={ButtonType.Submit} color={ButtonColor.Success}>Submit Form</Button>
                        <Button type={ButtonType.Reset} color={ButtonColor.Warning}>Reset Form</Button>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonTypeCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Button as Link
            </Heading>

            <Text className="mb-4">
                Use the <Text el="strong">el</Text> prop (type: <Text el="strong">ButtonTag</Text>) to render a button as different HTML elements. The <Text el="strong">ButtonTag</Text> type accepts <Text el="strong">"button"</Text> (default) or <Text el="strong">"a"</Text> for anchor tags. This is useful for navigation while maintaining button styling. When using <Text el="strong">el="a"</Text>, you can pass standard anchor attributes like <Text el="strong">href</Text>, <Text el="strong">target</Text>, and <Text el="strong">rel</Text>.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button el="a" href="https://getbootstrap.com" target="_blank" rel="noopener noreferrer" color={ButtonColor.Primary}>
                            External Link
                        </Button>
                        <Button el="a" href="#section" color={ButtonColor.Secondary}>
                            Internal Link
                        </Button>
                        <Button el="a" href="#disabled" color={ButtonColor.OutlineSuccess} className="disabled">
                            Disabled Link
                        </Button>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonAsLinkCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    )
}

export default ButtonPage;


const ButtonColorCode = `<Stack gap={Gap.Gap2}>
    <Button color={ButtonColor.Primary}>Primary</Button>
    <Button color={ButtonColor.Secondary}>Secondary</Button>
    <Button color={ButtonColor.Success}>Success</Button>
    <Button color={ButtonColor.Danger}>Danger</Button>
    <Button color={ButtonColor.Warning}>Warning</Button>
    <Button color={ButtonColor.Info}>Info</Button>
    <Button color={ButtonColor.Light}>Light</Button>
    <Button color={ButtonColor.Dark}>Dark</Button>
</Stack>`;

const ButtonOutlineColorCode = `<Stack gap={Gap.Gap2}>
    <Button color={ButtonColor.OutlinePrimary}>Primary</Button>
    <Button color={ButtonColor.OutlineSecondary}>Secondary</Button>
    <Button color={ButtonColor.OutlineSuccess}>Success</Button>
    <Button color={ButtonColor.OutlineDanger}>Danger</Button>
    <Button color={ButtonColor.OutlineWarning}>Warning</Button>
    <Button color={ButtonColor.OutlineInfo}>Info</Button>
    <Button color={ButtonColor.OutlineLight}>Light</Button>
    <Button color={ButtonColor.OutlineDark}>Dark</Button>
</Stack>`;

const ButtonLGSizeCode = `<Stack gap={Gap.Gap2}>
    <Button size={ButtonSize.Lg} color={ButtonColor.Primary}>Primary</Button>
    <Button size={ButtonSize.Lg} color={ButtonColor.OutlineSecondary}>Secondary</Button>
</Stack>`;

const ButtonSMSizeCode = `<Stack gap={Gap.Gap2}>
    <Button size={ButtonSize.Sm} color={ButtonColor.Primary}>Primary</Button>
    <Button size={ButtonSize.Sm} color={ButtonColor.OutlineSecondary}>Secondary</Button>
</Stack>`;

const ButtonDisabledCode = `<Stack gap={Gap.Gap2}>
    <Button color={ButtonColor.Primary} disabled={true}>Primary</Button>
    <Button color={ButtonColor.Secondary} disabled={true}>Secondary</Button>
    <Button color={ButtonColor.OutlinePrimary} disabled={true}>Primary</Button>
    <Button color={ButtonColor.OutlineSecondary} disabled={true}>Secondary</Button>
</Stack>`;

const ButtonTypeCode = `<Stack gap={Gap.Gap2}>
    <Button type={ButtonType.Button} color={ButtonColor.Primary}>Button (Default)</Button>
    <Button type={ButtonType.Submit} color={ButtonColor.Success}>Submit Form</Button>
    <Button type={ButtonType.Reset} color={ButtonColor.Warning}>Reset Form</Button>
</Stack>`;

const ButtonAsLinkCode = `// el prop accepts ButtonTag type: "button" | "a"
<Stack gap={Gap.Gap2}>
    <Button el="a" href="https://getbootstrap.com" target="_blank" rel="noopener noreferrer" color={ButtonColor.Primary}>
        External Link
    </Button>
    <Button el="a" href="#section" color={ButtonColor.Secondary}>
        Internal Link
    </Button>
    <Button el="a" href="#disabled" color={ButtonColor.OutlineSuccess} className="disabled">
        Disabled Link
    </Button>
</Stack>`;


